import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiMapPin, FiArrowRight } from 'react-icons/fi';
import { useBooking } from '../../context/BookingContext';
import { getActiveLocations } from '../../services/vehicleService';
import TripTypeSelector from './TripTypeSelector';
import LocationSelector from './LocationSelector';
import DateSelector from './DateSelector';
import Loader from '../../components/Loader';
import ErrorMessage from '../../components/ErrorMessage';
import './BookingWidget.css';

const BookingWidget = () => {
  const { booking, updateBooking } = useBooking();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState('');
  const navigate = useNavigate();

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getActiveLocations();
      setLocations(res?.data || []);
    } catch (err) {
      console.error(err);
      setError('Unable to load service locations. Please retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleSubmit = () => {
    setValidationError('');

    if (!booking.pickup?.name) {
      setValidationError('Please select a pickup location.');
      return;
    }
    if (booking.tripType !== 'rental' && !booking.dropoff?.name) {
      setValidationError('Please select a drop location.');
      return;
    }
    if (
      booking.tripType !== 'rental' &&
      booking.pickup?.name === booking.dropoff?.name
    ) {
      setValidationError('Pickup and drop cannot be the same.');
      return;
    }
    if (!booking.date) {
      setValidationError('Please select a travel date.');
      return;
    }
    if (booking.tripType === 'outstation' && !booking.pickupTime) {
      setValidationError('Please select a pickup time.');
      return;
    }
    if (booking.tripType === 'instant' && !booking.pickupTime) {
      setValidationError('Please select a pickup time.');
      return;
    }
    const isOutstationRoundTrip =
      booking.tripType === 'outstation' && booking.rideMode === 'round_trip';
    const requiresReturnDate =
      booking.tripType === 'round_trip' || isOutstationRoundTrip;
    if (
      requiresReturnDate &&
      (!booking.returnDate || (isOutstationRoundTrip && !booking.returnDate.includes('T')))
    ) {
      setValidationError(
        isOutstationRoundTrip
          ? 'Please select a return date and time.'
          : 'Please select a return date.'
      );
      return;
    }
    if (
      isOutstationRoundTrip &&
      booking.returnDate <= `${booking.date}T${booking.pickupTime}`
    ) {
      setValidationError('Return time must be after the pickup time.');
      return;
    }
    navigate('book');
  };

  if (loading) return <div className="bw-card"><Loader /></div>;
  if (error)
    return (
      <div className="bw-card">
        <ErrorMessage message={error} onRetry={fetchLocations} />
      </div>
    );

  const isRental = booking.tripType === 'rental';
  const showDrop = !isRental;
  const ctaLabel = booking.tripType === 'instant'
    ? 'Find Available Cabs'
    : booking.tripType === 'rental' || booking.tripType === 'outstation'
      ? 'Show Packages'
      : 'Search Cabs';

  return (
    <div className="bw-card">
      <TripTypeSelector />

      <div className="bw-fields">
        <LocationSelector
          label="Pickup"
          placeholder="Enter pickup location"
          icon={<FiMapPin />}
          locations={locations}
          value={booking.pickup}
          excludeId={booking.dropoff?.id}
          onChange={(loc) => updateBooking('pickup', loc)}
        />

        {showDrop && (
          <LocationSelector
            label="Drop"
            placeholder="Enter drop location"
            icon={<FiArrowRight />}
            locations={locations}
            value={booking.dropoff}
            excludeId={booking.pickup?.id}
            onChange={(loc) => updateBooking('dropoff', loc)}
          />
        )}

        <DateSelector />
      </div>

      {validationError && <p className="bw-error">{validationError}</p>}

      <button className="bw-cta" onClick={handleSubmit}>
        <FiSearch /> {ctaLabel}
      </button>
    </div>
  );
};

export default BookingWidget;